/**
 * The share loop: take a picture of the world, sign it, and hand it to the pilot.
 *
 * ```
 *   key `6` / camera button ──► request ──┐
 *                                         │  (one frame later, inside the frame)
 *                        afterFrame ──────┴──► readback ──► compose ──► watermark
 *                                                              │
 *                                     Web Share API ◄──────────┼──────────► download
 *                                                              │
 *                                                       "copy share text"
 * ```
 *
 * ## The one hard constraint: *when* the canvas may be read
 *
 * A WebGPU or WebGL2 canvas is not a picture you can ask for whenever you like. Its drawing buffer
 * is valid only until the browser composites the frame, and after that a readback returns
 * transparent black — the canvas is created without `preserveDrawingBuffer` on purpose, because
 * preserving it costs a full-screen copy every frame forever in exchange for a feature used once a
 * session.
 *
 * So a click cannot capture. A click sets {@link ShareController.request} and the *frame loop*
 * captures, from the after-frame slot, synchronously after `render()` and before the browser gets
 * the canvas back. That is the same slot `game/screenshotRun.ts` uses for the scripted shots, and
 * it is the reason both of these live in `game/` next to the loop rather than in `ui/`.
 *
 * ## What is in the picture, and what is not
 *
 * The canvas, and only the canvas. The flight HUD, the help strip, the airport pill, the perf
 * overlay and the fighter HUD are all separate elements stacked over it — DOM in four cases and a
 * second `<canvas>` in the fifth — so none of them are in the readback and there is no "hide the
 * overlays first" step that can be forgotten or get out of step with a new overlay. A screenshot is
 * the world, which is what anyone wants to share.
 *
 * ## Why the share sheet still works a frame later
 *
 * `navigator.share()` requires transient user activation, and this deliberately defers the capture
 * past the click that asked for it. That is safe by a wide margin: transient activation lasts on
 * the order of five seconds, and one frame is eight milliseconds. `canvas.toBlob` adds another
 * asynchronous hop for the same reason and with the same margin.
 *
 * `updateFrame(alpha)`: yes — called from the loop's after-frame slot. It reads one boolean and
 * returns on every frame that is not a capture, so an idle session pays nothing for it.
 */

import { SITE_DOMAIN, PRODUCT_NAME, screenshotFileName, shareText, type SharedFlight } from '../brand.js';

/**
 * How the shot was delivered, so the toast can say something true about it.
 *
 * `cancelled` is its own outcome rather than being folded into either neighbour. It is not a
 * failure — nothing went wrong — and it is not a share, because nothing left the device; saying
 * either would be a small lie told by a status message, which is the kind users notice.
 */
export type ShareOutcome = 'shared' | 'downloaded' | 'cancelled' | 'failed';

/**
 * Where the source rectangle sits when an image of one aspect ratio is drawn into a frame of
 * another, filling it — the CSS `object-fit: cover` rule, as numbers.
 *
 * Cover rather than contain: a social card is a fixed 1200×630 letterbox and a 16:9 screenshot
 * dropped into it with `contain` gets two black bars, which look like a rendering failure to
 * everyone who has not been told otherwise. Cropping the top and bottom of a sky is free.
 *
 * Pure, and separated out because the arithmetic has an obvious wrong version — dividing the
 * aspect ratios the other way up crops the wrong axis, and the result is a *plausible* picture
 * that is silently wrong for every input where the two ratios differ in the other direction.
 */
export function coverFit(
  sourceWidthPx: number,
  sourceHeightPx: number,
  targetWidthPx: number,
  targetHeightPx: number,
): { x: number; y: number; widthPx: number; heightPx: number } {
  if (sourceWidthPx <= 0 || sourceHeightPx <= 0 || targetWidthPx <= 0 || targetHeightPx <= 0) {
    return { x: 0, y: 0, widthPx: Math.max(0, sourceWidthPx), heightPx: Math.max(0, sourceHeightPx) };
  }
  const sourceAspect = sourceWidthPx / sourceHeightPx;
  const targetAspect = targetWidthPx / targetHeightPx;

  if (sourceAspect > targetAspect) {
    // Source is wider than the frame: keep full height, crop the sides, centred.
    const widthPx = sourceHeightPx * targetAspect;
    return { x: (sourceWidthPx - widthPx) * 0.5, y: 0, widthPx, heightPx: sourceHeightPx };
  }
  // Source is taller: keep full width, crop top and bottom, centred.
  const heightPx = sourceWidthPx / targetAspect;
  return { x: 0, y: (sourceHeightPx - heightPx) * 0.5, widthPx: sourceWidthPx, heightPx };
}

/** Every size the watermark needs, in pixels, for an output of a given height. */
export interface WatermarkLayout {
  /** Wordmark cap height [px]. */
  namePx: number;
  /** Domain line cap height [px]. */
  domainPx: number;
  /** Gap from the image edge to the block [px]. */
  marginPx: number;
  /** Baseline-to-baseline distance between the two lines [px]. */
  leadingPx: number;
  /** Letter spacing on the wordmark [px]. */
  trackingPx: number;
  /** Radius of the shadow that lifts the text off a bright sky [px]. */
  shadowPx: number;
}

/**
 * Scale the watermark to the output.
 *
 * Everything is a fraction of the image **height**, not of its width or of its diagonal, so the
 * mark occupies the same share of the frame at 1200×630 as it does at 3840×2160 and a card and a
 * 4K screenshot look like they came from the same product. Clamped at both ends because the
 * fraction alone gives a 6 px wordmark on a thumbnail and a 90 px one on a poster.
 */
export function watermarkLayout(heightPx: number): WatermarkLayout {
  /*
   * The clamps are guard rails, not the rule. 2.6 % of the height is the right size at every
   * output this actually sees — a 630 px card, a 1080p window, a 4K screenshot — and the bounds
   * exist only so that a thumbnail does not get an illegible 5 px mark and a pathological input
   * does not get a 200 px one. Set the ceiling too low and it starts governing the 4K case, where
   * it makes the mark *shrink* relative to the frame; `screenshot.test.ts` pins that it does not.
   */
  const namePx = Math.min(64, Math.max(13, heightPx * 0.026));
  return {
    namePx,
    domainPx: namePx * 0.62,
    marginPx: Math.min(44, Math.max(14, heightPx * 0.034)),
    leadingPx: namePx * 1.32,
    trackingPx: namePx * 0.16,
    shadowPx: namePx * 0.7,
  };
}

/**
 * Paint the corner mark onto a 2D context whose transform is the identity.
 *
 * Bottom-right, because that is the corner a flight simulator's picture is least likely to have
 * anything in: the horizon runs across the middle, the aircraft sits centre or centre-left in
 * every camera the sim has, and the bottom-right is ground or water in almost every frame.
 *
 * The shadow is not decoration. Half these images are of a sky at golden hour, which is the one
 * background pale text disappears into completely — and the other half are of a mountain at dusk,
 * where dark text disappears instead. A soft dark shadow under light text survives both, and it
 * is cheaper and far more predictable than measuring the underlying pixels.
 */
export function drawWatermark(
  context: CanvasRenderingContext2D,
  widthPx: number,
  heightPx: number,
  layout: WatermarkLayout = watermarkLayout(heightPx),
): void {
  context.save();
  context.textAlign = 'right';
  context.textBaseline = 'alphabetic';

  const right = widthPx - layout.marginPx;
  const baseline = heightPx - layout.marginPx;

  context.shadowColor = 'rgba(0, 0, 0, 0.55)';
  context.shadowBlur = layout.shadowPx;
  context.shadowOffsetY = layout.shadowPx * 0.12;

  // The domain sits under the wordmark, dimmer, so the eye reads the name first and the address
  // only if it is looking for it — which is the right order for something stamped on a picture.
  context.font = `500 ${layout.domainPx}px ui-monospace, "SF Mono", Menlo, Consolas, monospace`;
  context.fillStyle = 'rgba(232, 240, 248, 0.72)';
  context.fillText(SITE_DOMAIN, right, baseline);

  /*
   * `letterSpacing` is a 2D-context property rather than part of the font shorthand, and it is not
   * universally supported — Safari gained it in 17. Assigning to a property a context does not
   * have is a silent no-op rather than a throw, so the fallback is simply an untracked wordmark,
   * which is a slightly tighter mark and not a broken one.
   */
  (context as CanvasRenderingContext2D & { letterSpacing?: string }).letterSpacing =
    `${layout.trackingPx.toFixed(2)}px`;
  context.font = `250 ${layout.namePx}px system-ui, -apple-system, "Segoe UI", sans-serif`;
  context.fillStyle = 'rgba(255, 255, 255, 0.94)';
  context.fillText(PRODUCT_NAME.toUpperCase(), right, baseline - layout.leadingPx);

  context.restore();
}

export interface ComposeOptions {
  /** Output width [px]. Defaults to the source canvas' backing-store width. */
  widthPx?: number;
  /** Output height [px]. Defaults to the source canvas' backing-store height. */
  heightPx?: number;
  /** Stamp the corner mark. On by default — a shared picture should say where it came from. */
  watermark?: boolean;
}

/**
 * Draw the live canvas into a fresh 2D canvas at the requested size, watermarked.
 *
 * A copy rather than watermarking in place, for two reasons that are both about not lying: the
 * live canvas belongs to the renderer and drawing on it would put the mark into the *next* frame
 * the pilot flies, and the social card needs a different aspect ratio from the window it was
 * captured in, which is only expressible as a resample.
 *
 * Returns `null` when a 2D context cannot be had, which is the honest answer on a browser that has
 * exhausted its canvas budget — the caller reports a failure rather than saving a blank file.
 */
export function composeShot(
  source: HTMLCanvasElement,
  options: ComposeOptions = {},
): HTMLCanvasElement | null {
  const widthPx = Math.max(1, Math.round(options.widthPx ?? source.width));
  const heightPx = Math.max(1, Math.round(options.heightPx ?? source.height));

  const target = document.createElement('canvas');
  target.width = widthPx;
  target.height = heightPx;
  const context = target.getContext('2d');
  if (context === null) return null;

  const crop = coverFit(source.width, source.height, widthPx, heightPx);
  context.drawImage(
    source,
    crop.x,
    crop.y,
    crop.widthPx,
    crop.heightPx,
    0,
    0,
    widthPx,
    heightPx,
  );

  if (options.watermark !== false) drawWatermark(context, widthPx, heightPx);
  return target;
}

/** `canvas.toBlob` as a promise. Resolves `null` when the encoder refuses. */
export function canvasToPng(canvas: HTMLCanvasElement): Promise<Blob | null> {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), 'image/png');
  });
}

/**
 * Can this browser put an image file into a share sheet?
 *
 * Three separate checks, because two of them pass on browsers that cannot do it. Desktop Chrome
 * has `navigator.share` and will happily share a *link*; `canShare` exists more widely still. Only
 * `canShare({ files })` answers the question actually being asked, and it has to be asked with a
 * real `File`, which is why this takes one.
 */
export function canShareFile(file: File, navigatorLike: Navigator = navigator): boolean {
  const candidate = navigatorLike as Navigator & {
    canShare?: (data: ShareData) => boolean;
    share?: (data: ShareData) => Promise<void>;
  };
  if (typeof candidate.share !== 'function' || typeof candidate.canShare !== 'function') return false;
  try {
    return candidate.canShare({ files: [file] });
  } catch {
    // Some implementations throw on an unexpected `ShareData` shape rather than returning false.
    return false;
  }
}

/** Save a blob to the user's downloads under `fileName`. */
export function downloadBlob(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  // Not appended to the document: a detached anchor's `click()` starts the download in every
  // browser that supports the attribute, and appending one risks a visible layout flash.
  link.click();
  // Freed on a later task rather than immediately — revoking synchronously has been observed to
  // cancel the download in flight on some builds, and one object URL for one frame is nothing.
  globalThis.setTimeout(() => URL.revokeObjectURL(url), 30_000);
}

export interface ShareControllerOptions {
  canvas: HTMLCanvasElement;
  /** Where the aircraft is, for the file name and the post line. Called at capture time. */
  place?: () => string | null;
  /** The flight a shared link should open into. See `SharedFlight` — an allow-list, not the URL. */
  flight?: () => SharedFlight | null;
  /** Told what happened, so the UI can say so. */
  onResult?: (outcome: ShareOutcome, detail: string) => void;
  /** Injected by the tests; the real one is `Date`. */
  now?: () => Date;
}

/**
 * Owns the "take a picture" request and everything that happens to the picture afterwards.
 *
 * Deliberately does not own a button or a key binding — `main.ts` wires those, exactly as it wires
 * every other binding, so the whole of "what does key 6 do" is readable in one place.
 */
export class ShareController {
  private readonly options: ShareControllerOptions;
  private pending = false;
  private busy = false;
  /** The last shot, kept so the toast's buttons can act on it without re-capturing. */
  private lastBlob: Blob | null = null;
  private lastFileName = '';
  private lastPlace: string | null = null;
  private lastFlight: SharedFlight | null = null;

  constructor(options: ShareControllerOptions) {
    this.options = options;
  }

  /** Ask for a capture on the next frame. Ignored while one is already in flight. */
  request(): boolean {
    if (this.pending || this.busy) return false;
    this.pending = true;
    return true;
  }

  get isBusy(): boolean {
    return this.pending || this.busy;
  }

  /** The post line for the last shot — what "copy share text" copies. */
  get shareLine(): string {
    return shareText(this.lastPlace, this.lastFlight);
  }

  /**
   * Called from the loop's after-frame slot. Does nothing at all unless a capture was requested.
   *
   * The readback happens here and nowhere else; see the module header for why that is not a
   * preference.
   */
  updateFrame(): void {
    if (!this.pending) return;
    this.pending = false;
    this.busy = true;

    this.lastPlace = this.options.place?.() ?? null;
    this.lastFlight = this.options.flight?.() ?? null;
    const composed = composeShot(this.options.canvas);
    if (composed === null) {
      this.busy = false;
      this.options.onResult?.('failed', 'the browser would not give us a drawing context');
      return;
    }

    const stamp = (this.options.now ?? (() => new Date()))();
    this.lastFileName = screenshotFileName(stamp, this.lastPlace);
    void this.deliver(composed);
  }

  /** Put the last shot into the share sheet again, from a fresh user gesture. */
  async reshare(): Promise<ShareOutcome> {
    if (this.lastBlob === null) return 'failed';
    return this.dispatch(this.lastBlob, this.lastFileName);
  }

  /** Save the last shot again — the escape hatch when a share sheet was dismissed. */
  save(): boolean {
    if (this.lastBlob === null) return false;
    downloadBlob(this.lastBlob, this.lastFileName);
    return true;
  }

  private async deliver(composed: HTMLCanvasElement): Promise<void> {
    try {
      const blob = await canvasToPng(composed);
      if (blob === null) {
        this.options.onResult?.('failed', 'the PNG encoder returned nothing');
        return;
      }
      this.lastBlob = blob;
      const outcome = await this.dispatch(blob, this.lastFileName);
      this.options.onResult?.(outcome, outcome === 'downloaded' ? this.lastFileName : '');
    } finally {
      this.busy = false;
    }
  }

  /**
   * Share sheet where there is one, a download where there is not.
   *
   * The order matters on a phone, where a download lands in a folder the pilot then has to go
   * find, and the share sheet is the whole point. A dismissed sheet is **not** a failure — the
   * spec rejects with `AbortError` when the user closes it, and turning a deliberate cancellation
   * into a surprise download in the pilot's files is the sort of thing that makes people stop
   * pressing buttons.
   */
  private async dispatch(blob: Blob, fileName: string): Promise<ShareOutcome> {
    const file = new File([blob], fileName, { type: 'image/png' });
    if (canShareFile(file)) {
      try {
        await navigator.share({
          files: [file],
          title: PRODUCT_NAME,
          text: shareText(this.lastPlace, this.lastFlight),
        });
        return 'shared';
      } catch (error) {
        if (error instanceof DOMException && error.name === 'AbortError') return 'cancelled';
        // Anything else — a permission policy, a platform that lied about `canShare` — falls
        // through to the download, which always works.
      }
    }
    downloadBlob(blob, fileName);
    return 'downloaded';
  }
}
